/**
 * Dependencies.
 */
import { debounce } from 'lodash-es';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';

import { ServicePointModel } from '@/api/service-point/service-point/service-point.model';

/**
 * Component.
 */
@Component({ name: 'select-pick-up-component' })
export default class extends Vue {
  /**
   * Property `isSelectPickupVisible`.
   */
  isSelectPickupVisible = false;

  /**
   * Property `currentPickUp`.
   */
  currentPickUp: ServicePointModel = null;

  /**
   * Property `isSearching`.
   */
  isSearching = false;

  /**
   * Property `searchPickUpName`.
   */
  searchPickUpName = null;

  /**
   * Property `searchFilter`.
   */
  searchFilter = debounce(this.handleSearchTrigger, 1000);

  /**
   * Property `currentPickUpPoints`.
   */
  currentPickUpPoints = [];

  /**
   * Property `searchString`.
   */
  searchString = '';

  /**
   * Property `currentPickUpPointId`.
   */
  @Prop({ default: null, type: Number })
  currentPickUpPointId: number;

  /**
   * Property `pickUpPoints`.
   */
  @Prop({ default: () => [], type: Array })
  pickUpPoints: ServicePointModel[];

  /**
   * Getter `pickUpPoint`.
   */
  get pickUpPoint() {
    const { code, id, name } = this.currentPickUp || {};

    return { code, id, name };
  }

  /**
   * Lifecycle mounted hook.
   */
  mounted() {
    const { pickUpPoints = [], currentPickUpPointId = null } = this || {};

    const [firstPickUpPoint] = pickUpPoints;
    const { id: idOfTheFirstPickUpPoint } = firstPickUpPoint || {};

    const pickUpId = currentPickUpPointId || idOfTheFirstPickUpPoint;

    this.currentPickUp = pickUpPoints.find(({ id }) => id === pickUpId) || null;

    this.currentPickUpPoints = [...pickUpPoints];
  }

  /**
   * Handle toggle select.
   */
  handleToggleSelect() {
    if (this.currentPickUpPoints?.length > 1 || this.searchString) {
      this.isSelectPickupVisible = !this.isSelectPickupVisible;
    }
  }

  /**
   * Handle search trigger.
   */
  handleSearchTrigger(value) {
    this.searchString = value || '';
    this.isSearching = true;

    this.$emit('load-more', { page: 1, searchString: this.searchString });
  }

  /**
   * Handle select pick up point.
   */
  handleSelectPickUpPoint(id: number) {
    const { pickUpPoints = [] } = this || {};
    const pickUpPoint = pickUpPoints.find(
      ({ id: pickUpPointId }) => pickUpPointId === id
    );

    this.$emit('selected', { pickUpPoint });

    this.isSelectPickupVisible = false;
  }

  /**
   * Handle scrolling.
   */
  handleScrolling(event) {
    const element = event.target;

    if (element.offsetHeight + element.scrollTop >= element.scrollHeight) {
      this.$emit('load-more', { searchString: this.searchString });
    }
  }

  /**
   * Handle current pick up point id change.
   */
  @Watch('currentPickUpPointId')
  handleCurrentPickUpPointIdChange(currentPickUpPointId) {
    const currentPickUp = this.pickUpPoints.find(
      ({ id }) => id === currentPickUpPointId
    );

    if (currentPickUp) {
      this.currentPickUp = currentPickUp;
    }
  }

  /**
   * Handle pick up points change.
   */
  @Watch('pickUpPoints')
  handlePickUpPointChange(pickUpPoints) {
    this.currentPickUpPoints = [...(pickUpPoints || [])];
    this.isSearching = false;
  }

  /**
   * Handle search pick up name change.
   */
  @Watch('searchPickUpName')
  handleSearchPickUpNameChange() {
    this.isSearching = this.isSelectPickupVisible;
  }
}
